<template>
  <div class="line-graph">
    <p v-if="errorMessage" class="error-label text-center">{{errorMessage}}</p>
    <div id="line" class="line-graph-wrapper">
      <tool-tip v-show="showInfo"
                :coordinates="toolTipCoordinates"
                :textLines="toolTipContent"
                yIsBottom></tool-tip>

      <svg class="line-graph-svg"
           :id="`line_graph_svg_${_uid}`"
           :height="height + marginDoubled"
           :width="width"
           @mouseleave="hideToolTip">
        <g class="line-graph-group"
           ref="lineGraph">
          <g class="line-circle">
            <circle :class="['line-indicator', {show: showInfo}]"
                    r="4"
                    :cx="circleTrackX"
                    :cy="circleTrackY"></circle>
          </g>

          <g class="line-path-section">
            <path class="line" :d="dPathLine"></path>
          </g>

          <g class="scale-axis x-axis"
             text-anchor="middle"
             :transform="`translate(${margin}, ${height})`">
            <g v-for="tick in xTicks"
               :key="tick.value"
               :transform="`translate(${tick.xScaleTick}, 0)`">
              <text>
                <tspan v-for="word in tick.words" x="0" dy="1em">{{word}}</tspan>
              </text>
            </g>
          </g>
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
  import * as d3 from 'd3'
  import ToolTip from '../shared/ToolTip'

  export default {
    props: {
      barSelectorData: Array,
      dateTicks: Array,
      graphData: Array,
      numOfTicks: {
        default: 12,
        type: [Number, String]
      },
      toolTipContent: [Array, String],
      wrapperWidth: [Number, String],
      xScaleDomain: Array,
      xSelector: [Array, String],
      ySelector: [Array, String],
    },

    name: 'LineGraph',

    components: {
      ToolTip
    },

    data() {
      return {
        circleTrackX: 0,
        circleTrackY: 0,
        dPathLine: null,
        errorMessage: null,
        height: null,
        margin: 20,
        showInfo: false,
        toolTipCoordinates: null,
        width: null,
        xScale: null,
        xTicks: null,
        yScale: null,
      }
    },

    methods: {
      buildXticks() {
        let xTicksScale = d3.scalePoint().range([0, this.width - this.marginDoubled]),
          ticksDomain = this.dateTicks ? this.dateTicks : this.graphData.map((item) => item[this.xSelector])
        xTicksScale.domain(ticksDomain)
        this.xTicks = xTicksScale.domain().map(t => this.createTickObject(t, xTicksScale(t)) )
      },

      configureToolTipHover() {
        const view = this
        if (this.graphData.length === 1) { return this.permanentToolTip() }
        if (this.graphData.length > 1) { return d3.select(`#line_graph_svg_${this._uid}.line-graph-svg`).on('mousemove', view.showToolTip) }
      },

      createTickObject(tick, xScaleTick) {
        return {
          value: tick,
          words: tick.split(' '),
          xScaleTick: xScaleTick
        }
      },

      drawGraph() {
        if (this.graphData) {
          this.errorMessage = null

          this.xScale = d3.scalePoint()
            .range([0, this.width])
            .domain(this.graphData.map(item => item[this.xSelector]))

          this.yScale = d3.scaleLinear()
            .range([this.heightNoMargin, 0])
            .domain( d3.extent(this.graphData, d => d[this.ySelector]) )
          this.drawLine()
          this.buildXticks()
          this.configureToolTipHover()
        } else {
          this.errorMessage = 'data not available'
        }
      },

      drawLine() {
        const line = d3.line()
          .curve(d3.curveMonotoneX)
          .x(d => this.xScale(d[this.xSelector]))
          .y(d => this.yScale(d[this.ySelector]))
        this.dPathLine = line(this.graphData)
      },

      findY($path, x) {
        // taken from: https://stackoverflow.com/a/47935325
        let pathLength = $path.getTotalLength(),
          start = 0,
          end = pathLength,
          target = (start + end) / 2

        x = Math.max(x, $path.getPointAtLength(0).x)
        x = Math.min(x, $path.getPointAtLength(pathLength).x)

        while (target >= start && target <= pathLength) {
          let pos = $path.getPointAtLength(target)
          if (Math.abs(pos.x - x) < 0.001) {
            return pos.y
          } else if (pos.x > x) {
            end = target
          } else {
            start = target
          }
          target = (start + end) / 2
        }
      },

      hideToolTip() {
        if (this.graphData.length > 1) { this.showInfo = false }
      },

      permanentToolTip() {
        d3.select(`line_graph_svg_${this._uid}.line-graph-svg`).on('mousemove', null)
        const point = this.graphData[0]
        this.circleTrackX = this.xScale(point[this.xSelector])
        this.circleTrackY = this.yScale(point[this.ySelector])

        this.setToolTipCoords(this.graphData[0], {nudgeUpRight: true, x: this.circleTrackX, y: this.circleTrackY})
      },

      setGraphDimensions() {
        this.width = this.wrapperWidth
        this.height = (this.wrapperWidth * (3/4))
      },

      setToolTipCoords(plotItem, coordinates) {
        this.$emit('setToolTipContent', plotItem)
        this.showInfo = true
        this.toolTipCoordinates = coordinates
      },

      showToolTip() {
        const $path = d3.select(`#line_graph_svg_${this._uid} .line`).node(),
          $line = this.$refs.lineGraph,
          step = this.width / this.graphData.length,
          mouseX = d3.mouse($line)[0],
          intervals = d3.range(0, this.width, step)

        let y = this.findY($path, mouseX),
            bisectIndex = d3.bisectLeft(intervals, mouseX)

        this.circleTrackX = mouseX
        this.circleTrackY = mouseX >= 0 ? y : this.yScale(this.graphData[0].amount)

        if (mouseX >= 0) {
          let cx = this.circleTrackX + this.margin / 2,
            cy = this.circleTrackY - this.margin
          if (bisectIndex == 0) {
            bisectIndex = 1;
          }
          this.setToolTipCoords(this.graphData[bisectIndex - 1], {x: cx, y: cy})
        }
      },
    },

    created(){
      this.setGraphDimensions()
    },

    mounted() {
      this.drawGraph()
    },

    computed: {
      heightNoMargin: function() {
        return this.height - this.marginDoubled
      },

      marginDoubled: function() {
        return this.margin * 2
      },
    },

    watch: {
      graphData: function() {
        this.drawGraph()
      },

      wrapperWidth: function() {
        this.setGraphDimensions()
        if (this.graphData) { this.drawGraph() }
      },
    }
  }
</script>
